import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const EMOJI_POOL = [
  { emoji: '🔥', weight: 4 },
  { emoji: '⚡', weight: 3 },
  { emoji: '🏆', weight: 2 },
  { emoji: '🏅', weight: 1 },
];

const LARGE_EMOJI = new Set(['🏆', '🏅']);
const FLY_DURATION_MS = 5000;
const FLY_DURATION_FAST_MS = 800;
/** Gentle ease-out — explosion stage eases in, then drifts. */
const EASE = 'cubic-bezier(0.05, 0.72, 0.12, 1)';
const FONT_VMIN_RATIO = 5.4;
const FONT_MIN_PX = 28;
const FONT_MAX_PX = 54;

function pickEmoji() {
  const total = EMOJI_POOL.reduce((sum, item) => sum + item.weight, 0);
  let roll = Math.random() * total;
  for (let i = 0; i < EMOJI_POOL.length; i += 1) {
    roll -= EMOJI_POOL[i].weight;
    if (roll <= 0) return EMOJI_POOL[i].emoji;
  }
  return EMOJI_POOL[0].emoji;
}

function buildParticles(count, { minDist, maxDist, maxDelay }) {
  return Array.from({ length: count }, (_, index) => {
    const emoji = pickEmoji();
    return {
      id: `splash-${index}`,
      emoji,
      angle: Math.random() * 360,
      distVmin: minDist + Math.random() * (maxDist - minDist),
      delay: Math.random() * maxDelay,
      spin: (Math.random() > 0.5 ? 1 : -1) * (5 + Math.random() * 10),
      size: LARGE_EMOJI.has(emoji)
        ? 1.15 + Math.random() * 0.28
        : 1 + Math.random() * 0.32,
    };
  });
}

function particleTransform(angleDeg, distPx, scale, spinDeg = 0) {
  return `translate3d(-50%, -50%, 0) rotate(${angleDeg + spinDeg}deg) translate3d(0, ${-distPx}px, 0) scale(${scale})`;
}

function StreakEmojiParticle({ particle, fast }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof el.animate !== 'function') return undefined;

    const vmin = Math.min(window.innerWidth, window.innerHeight) / 100;
    const distPx = particle.distVmin * vmin;
    const duration = fast ? FLY_DURATION_FAST_MS : FLY_DURATION_MS;
    const { angle, delay, size, spin } = particle;

    const splashEnd = fast ? 0.56 : 0.68;
    const driftEnd = fast ? 0.72 : 0.78;

    const anim = el.animate(
      [
        {
          transform: particleTransform(angle, 0, size * 0.4),
          opacity: 0,
        },
        {
          transform: particleTransform(angle, distPx * 0.18, size * 0.78),
          opacity: 0.65,
          offset: splashEnd * 0.32,
        },
        {
          transform: particleTransform(angle, distPx * 0.38, size * 0.88),
          opacity: 0.82,
          offset: splashEnd * 0.58,
        },
        {
          transform: particleTransform(angle, distPx * 0.58, size * 0.96),
          opacity: 0.94,
          offset: splashEnd * 0.82,
        },
        {
          transform: particleTransform(angle, distPx * 0.76, size),
          opacity: 1,
          offset: splashEnd,
        },
        {
          transform: particleTransform(angle, distPx, size),
          opacity: 1,
          offset: driftEnd,
        },
        {
          transform: particleTransform(angle, distPx * 1.08, size * 0.94, spin),
          opacity: 0,
          offset: 1,
        },
      ],
      {
        duration,
        delay,
        easing: EASE,
        fill: 'forwards',
      },
    );

    return () => anim.cancel();
  }, [particle.angle, particle.delay, particle.distVmin, particle.size, particle.spin, fast]);

  const vmin = typeof window !== 'undefined'
    ? Math.min(window.innerWidth, window.innerHeight) / 100
    : 8;
  const fontSize = Math.min(FONT_MAX_PX, Math.max(FONT_MIN_PX, FONT_VMIN_RATIO * vmin)) * particle.size;

  return (
    <span
      ref={ref}
      className="streak-emoji-particle"
      style={{ fontSize: `${fontSize}px` }}
    >
      {particle.emoji}
    </span>
  );
}

function usePrefersReducedMotion() {
  return useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);
}

/**
 * Full-screen colored emoji burst (Web Animations API for smooth motion).
 */
export default function StreakEmojiSplash({ fast = false }) {
  const reducedMotion = usePrefersReducedMotion();
  const [portalRoot, setPortalRoot] = useState(null);

  useLayoutEffect(() => {
    setPortalRoot(document.querySelector('.streak-overlay'));
  }, []);

  const particles = useMemo(() => {
    const distRange = fast
      ? { minDist: 36, maxDist: 68, maxDelay: 55 }
      : { minDist: 48, maxDist: 92, maxDelay: 90 };
    const count = fast ? 12 : 20;
    return buildParticles(count, distRange);
  }, [fast]);

  if (reducedMotion || !portalRoot) {
    return null;
  }

  const splash = (
    <div className="streak-emoji-splash streak-emoji-splash--viewport" aria-hidden="true">
      {particles.map((particle) => (
        <StreakEmojiParticle key={particle.id} particle={particle} fast={fast} />
      ))}
    </div>
  );

  return createPortal(splash, portalRoot);
}
